/**
 * Huddle transcription mode, per device (docs/specs/on-device-transcription.md). Captions are made on the speaker's
 * device by default and only the text (with word timings) is sent; Bundle's server (Whisper on short audio clips) is
 * the automatic fallback when this device can't: the setting is off, no engine runs here, the engine doesn't speak the
 * caption language, its model isn't downloaded yet, storage or memory is short, or it failed. Joining never waits for a
 * model: the device starts on the server and switches at the next phrase once the model is ready.
 */
import type { CaptionWord, HuddleTranscription, PostCaptionRequest, TranscriptionReason, TranscriptionState } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";

/** Failures of the on-device engine in one call before the device stays on the server for the rest of it. */
export const MAX_ENGINE_FAILURES = 2;
/** Free storage needed to download a model: twice its size (download + unpack, and room left for the user). */
export const STORAGE_FACTOR = 2;
/** Most words sent with one phrase (PostCaptionRequest.words). */
export const MAX_CAPTION_WORDS = 400;
/** Longest word time accepted, in ms from the start of the phrase. */
export const MAX_WORD_MS = 10 * 60_000;

/** The on-device model: ready, downloading (with progress 0…1), not downloaded, or managed by the OS (nothing to fetch). */
export type ModelState = { kind: "ready" } | { kind: "downloading"; progress: number } | { kind: "missing"; bytes: number } | { kind: "system" };

/** What this device can do, as the platform measures it. */
export type TranscriptionDevice = {
  /** "Make captions on this device" (default on). */
  settingOn: boolean;
  /** The on-device engine here ("apple-speech", "voz", "android-speech", "web-speech"), null when none runs. */
  engine: string | null;
  /** Languages the engine speaks (BCP-47; "pt" matches "pt-BR"). */
  languages: string[];
  model: ModelState;
  /** Free storage in bytes, when known. */
  freeStorage?: number;
  /** Free memory in bytes, and what the engine needs, when known. */
  freeMemory?: number;
  memoryNeeded?: number;
  /** On-device failures in this call. */
  failures: number;
};

/** The modes the server takes, in preference order (JoinHuddleResponse.transcriptionModes; older servers: `transcription`). */
export function offeredModes(modes: HuddleTranscription[] | undefined, legacy: HuddleTranscription | undefined): HuddleTranscription[] {
  if (modes !== undefined && modes.length > 0) return modes;
  // Older servers take text captions always, and clips when they said "server".
  return legacy === "server" ? ["client", "server"] : ["client"];
}

/** Does an engine that speaks `languages` cover `locale` ("pt-BR" is covered by "pt-BR" or "pt")? */
export function speaksLanguage(languages: string[], locale: string): boolean {
  const want = locale.toLowerCase().replace(/_/g, "-");
  const base = want.split("-")[0]!;
  return languages.some((l) => {
    const have = l.toLowerCase().replace(/_/g, "-");
    return have === want || have === base;
  });
}

/** Why this device can't caption on the device right now (the first check that fails), or null when it can. */
export function deviceBlocker(device: TranscriptionDevice, locale: string): TranscriptionReason | null {
  if (!device.settingOn) return "setting_off";
  if (device.engine === null) return "unsupported";
  if (!speaksLanguage(device.languages, locale)) return "language";
  if (device.freeMemory !== undefined && device.memoryNeeded !== undefined && device.freeMemory < device.memoryNeeded) return "low_memory";
  const m = device.model;
  if (m.kind === "downloading") return "downloading";
  if (m.kind === "missing") {
    return device.freeStorage !== undefined && device.freeStorage < m.bytes * STORAGE_FACTOR ? "low_storage" : "not_downloaded";
  }
  if (device.failures >= MAX_ENGINE_FAILURES) return "failed";
  return null;
}

/**
 * The mode for my captions on this device: on the device when nothing blocks it, else the server when it takes clips.
 * With no server fallback the state is "client" with the reason, and captions are unavailable (`captionsAvailable`).
 */
export function selectTranscription(offered: HuddleTranscription[], device: TranscriptionDevice, locale: string): TranscriptionState {
  const blocker = deviceBlocker(device, locale);
  if (blocker === null && offered.includes("client")) return { mode: "client", engine: device.engine! };
  if (offered.includes("server")) return { mode: "server", reason: blocker ?? "unsupported", engine: "whisper" };
  return { mode: "client", reason: blocker ?? "unsupported" };
}

/**
 * Can I have captions at all? On the server, on the device with nothing blocking, or on the device once its model is
 * downloaded (a server without clips: the device captions when the download finishes).
 */
export function captionsAvailable(state: TranscriptionState): boolean {
  return state.mode === "server" || state.reason === undefined || state.reason === "downloading" || state.reason === "not_downloaded";
}

/** Did the state change in a way the server should hear about (UpdateHuddleRequest.transcription)? */
export function sameTranscription(a: TranscriptionState | undefined, b: TranscriptionState): boolean {
  return a !== undefined && a.mode === b.mode && a.reason === b.reason && a.engine === b.engine;
}

/** The note under my captions (design canvas "Caption status note"): where my captions are made, and why. */
export function captionNote(state: TranscriptionState, model: ModelState): MessageDescriptor {
  if (!captionsAvailable(state)) return msg("huddle.captions.note.none");
  if (state.reason === undefined) return msg("huddle.captions.note.device");
  switch (state.reason) {
    case "downloading":
      return msg("huddle.captions.note.preparing", { percent: model.kind === "downloading" ? Math.floor(Math.min(1, Math.max(0, model.progress)) * 100) : 0 });
    case "not_downloaded":
      return msg("huddle.captions.note.notDownloaded");
    case "language":
      return msg("huddle.captions.note.language");
    case "low_storage":
      return msg("huddle.captions.note.lowStorage", { size: model.kind === "missing" ? megabytes(model.bytes) : "" });
    case "failed":
      return msg("huddle.captions.note.failed");
    case "setting_off":
      return msg("huddle.captions.note.off");
    default:
      return msg("huddle.captions.note.unsupported");
  }
}

/** "337 MB" (decimal megabytes, as stores and OS settings show sizes). */
export function megabytes(bytes: number): string {
  return `${Math.round(bytes / 1_000_000)} MB`;
}

/** An engine's word, in seconds from the start of the audio it heard. */
export type EngineWord = { text: string; start: number; end: number };

/**
 * An engine's words → the caption's words: ms from the phrase's start (`phraseStart`, seconds on the same clock as the
 * words), trimmed, empty words dropped, times clamped to [0, MAX_WORD_MS] and never ending before they start or
 * starting before the previous word, at most MAX_CAPTION_WORDS.
 */
export function captionWords(words: EngineWord[], phraseStart: number): CaptionWord[] {
  const out: CaptionWord[] = [];
  let floor = 0;
  for (const w of words) {
    const text = w.text.trim();
    if (text === "" || !Number.isFinite(w.start) || !Number.isFinite(w.end)) continue;
    const start = Math.max(floor, clampMs((w.start - phraseStart) * 1000));
    const end = Math.max(start, clampMs((w.end - phraseStart) * 1000));
    out.push({ text: text.slice(0, 100), start, end });
    floor = start;
    if (out.length === MAX_CAPTION_WORDS) break;
  }
  return out;
}

function clampMs(ms: number): number {
  return Math.min(MAX_WORD_MS, Math.max(0, Math.round(ms)));
}

/**
 * One finished phrase → the request body (text and word timings only, never audio). `startedAt`/`now`: when the phrase
 * began and now, on this device's clock; the server turns `ageMs` into its own time. Null when there's no text.
 */
export function captionRequest(text: string, words: CaptionWord[], startedAt: number, now: number): PostCaptionRequest | null {
  const clean = text.replace(/\s+/g, " ").trim().slice(0, 1000);
  if (clean === "") return null;
  const req: PostCaptionRequest = { text: clean, ageMs: Math.min(MAX_WORD_MS, Math.max(0, Math.round(now - startedAt))) };
  if (words.length > 0) req.words = words;
  return req;
}

/**
 * Where a word is in a recording, in ms from the recording's start (`recordingStartedAt`, server clock), or null when
 * the line has no timing or the word came before the recording started.
 */
export function recordingOffset(lineStartedAt: number | undefined, word: CaptionWord, recordingStartedAt: number): number | null {
  if (lineStartedAt === undefined) return null;
  const at = lineStartedAt + word.start - recordingStartedAt;
  return at < 0 ? null : at;
}
